import type { Beat, BeatEdge, HolidayTabletConfig, ScenarioSpec } from "./types";
import { poolWalkableScenario } from "./pool-walkable.scenario";

const DANIEL_PROFILE: NonNullable<HolidayTabletConfig["profile"]> = {
  name: "Daniel Kovacs",
  title: "Partner — Corporate Strategy & Technology Investments",
  company: "Northbridge Capital Partners",
  location: "London, United Kingdom",
  connections: "500+",
  mutual: "Thomas Berger · Mutual connection",
  avatarUrl: "/cdn/sim/holiday-call/img/daniel-kovacs.webp",
  message:
    "Thanks Elena. Thomas mentioned your team is doing some interesting international expansion work. Hope you're finally getting some downtime after CyberSec Europe.",
  joined: "Joined LinkedIn March 2018 · 11 profile views this week",
  companyUrl: "northbrldge.capital",
  postBurstNote: "6 posts in the last 16 days · previous post April 2019",
  about:
    "Investor and adviser working with European technology companies on corporate strategy, acquisitions and AI-enabled operating models.",
  experience: [
    { role: "Partner", company: "Northbridge Capital Partners", years: "2021–present" },
    { role: "Investment Director", company: "Harcourt Ventures", years: "2017–2021" },
    { role: "Senior Consultant", company: "Benton & Rowe", years: "2012–2017" },
  ],
  posts: [
    {
      text: "Great discussions around AI transformation and European technology companies at CyberSec Europe.",
      meta: "3 days ago · 184 reactions · 23 comments",
      imageUrl: "/cdn/sim/holiday-call/img/daniel-conference.webp",
    },
    { text: "Looking forward to being back in Zurich next month.", meta: "5 days ago · 92 reactions · 11 comments" },
    { text: "Useful panel on finance automation and board reporting.", meta: "9 days ago · 41 reactions" },
    { text: "Excellent conversations in Zurich this week.", meta: "12 days ago · 67 reactions" },
  ],
  recommendations: [
    "Daniel combines strong strategic judgment with a practical understanding of technology.",
    "A thoughtful partner for complex international transactions.",
  ],
};

const POOL_ENV = {
  ...poolWalkableScenario.scene.environment,
  staticGltfProps: [
    {
      url: "/cdn/sim/pool-walkable/people-v13.glb",
      position: [0, 0, 0] as [number, number, number],
    },
  ],
};

const HACKER_ENV = {
  procedural: "hacker_room" as const,
  gltfUrl: "/cdn/sim/bar-walkable/props/hackerroom/hackerroom.glb",
  gltfScale: 0.28,
  gltfPosition: [0, 0.0633, 0] as [number, number, number],
  gltfRotationDeg: [0, 0, 0] as [number, number, number],
  gltfColliders: [
    { min: [-2.15, 0, -2.0] as [number, number, number], max: [-1.85, 3.0, 2.0] as [number, number, number] },
    { min: [1.85, 0, -2.0] as [number, number, number], max: [2.15, 3.0, 2.0] as [number, number, number] },
    { min: [-2.0, 0, -2.15] as [number, number, number], max: [2.0, 3.0, -1.85] as [number, number, number] },
    { min: [-2.0, 0, 1.85] as [number, number, number], max: [2.0, 3.0, 2.15] as [number, number, number] },
  ],
  enableSplat: false,
  enablePano: false,
  backdropColor: "#05060a",
  exposure: 0.9,
  screenQuad: {
    center: [-0.057, 1.477, -0.601] as [number, number, number],
    width: 0.198,
    height: 0.152,
    rotationDeg: 13,
    maxDistance: 5,
  },
  splat: {},
};

const BEACH_ENV = {
  procedural: "holiday_beach" as const,
  gltfUrl: "/cdn/sim/holiday-call/beach-bar.glb",
  gltfScale: 1,
  gltfPosition: [0, 0, 1.4] as [number, number, number],
  gltfRotationDeg: [0, 180, 0] as [number, number, number],
  gltfColliders: [
    { min: [-3.5, 0, 0.2] as [number, number, number], max: [3.5, 4.8, 0.55] as [number, number, number] },
    { min: [-3.5, 0, 6.9] as [number, number, number], max: [3.5, 4.8, 7.2] as [number, number, number] },
    { min: [-3.6, 0, 0.2] as [number, number, number], max: [-3.25, 4.8, 7.2] as [number, number, number] },
    { min: [3.25, 0, 0.2] as [number, number, number], max: [3.6, 4.8, 7.2] as [number, number, number] },
  ],
  staticGltfProps: [
    { url: "/cdn/sim/holiday-call/resort-crowd.glb", position: [0, 0, 0] as [number, number, number] },
  ],
  outdoor: {
    sunElevationDeg: 22,
    sunAzimuthDeg: 238,
    sunIntensity: 2.35,
    turbidity: 7,
    rayleigh: 1.7,
    shadows: true,
    shadowRadius: 24,
    ground: { color: "#d4b97a", size: 76, y: -0.17 },
    palms: { count: 26, radius: 23 },
  },
  beachSurround: true,
  water: {
    center: [0, -0.34, -25] as [number, number, number],
    width: 78,
    depth: 34,
    color: "#127ca1",
    distortionScale: 0.72,
    speed: 0.58,
    floorY: -2.5,
  },
  screenQuad: {
    center: [-1.15, 1.4, -1.57] as [number, number, number],
    width: 0.64,
    height: 0.39,
    rotationDeg: 180,
    tiltDeg: -8,
    maxDistance: 5,
  },
  enableSplat: false,
  enablePano: false,
  backdropColor: "#79b9d7",
  exposure: 1.02,
  splat: {},
};

const beats: Beat[] = [
  {
    id: "b_intro",
    type: "intro",
    title: "The Holiday Call",
    kicker: "A DAY AT THE RESORT",
    highlight: "company iPad",
    icon: "eye",
    ctaLabel: "Meet Elena",
    guide: {
      title: "Meet Elena Fischer",
      body: "You're Elena Fischer, Director of Finance Operations at Aureon Technologies. After several intense weeks you have taken four days off at a resort, and your company iPad came along just in case. At the pool, a page you opened before breakfast stops loading: the resort Wi-Fi has dropped.",
    },
  },
  { id: "b_pool_wide", type: "camera", waypointId: "wp_pool_wide", hold: true, guide: { title: "Elena at the pool", body: "Elena Fischer, Director of Finance Operations at Aureon, has taken four days off after an intense project. She plans to swim, read and ignore work. Her company iPad came along only for emergencies." } },
  { id: "b_pool_kids", type: "camera", waypointId: "wp_pool_kids", guide: { title: "Elena at the pool", body: "Elena Fischer, Director of Finance Operations at Aureon, has taken four days off after an intense project. She plans to swim, read and ignore work. Her company iPad came along only for emergencies." } },
  { id: "b_pool_behind", type: "camera", waypointId: "wp_pool_behind", guide: { title: "Elena at the pool", body: "Elena Fischer, Director of Finance Operations at Aureon, has taken four days off after an intense project. She plans to swim, read and ignore work. Her company iPad came along only for emergencies." } },
  { id: "b_pool_close", type: "camera", waypointId: "wp_pool_close", guide: { title: "Elena at the pool", body: "Elena Fischer, Director of Finance Operations at Aureon, has taken four days off after an intense project. She plans to swim, read and ignore work. Her company iPad came along only for emergencies." } },
  { id: "b_pool_shoulder", type: "camera", waypointId: "wp_pool_shoulder", guide: { title: "Elena at the pool", body: "Elena Fischer, Director of Finance Operations at Aureon, has taken four days off after an intense project. She plans to swim, read and ignore work. Her company iPad came along only for emergencies." } },
  { id: "b_pool_ipad", type: "camera", waypointId: "wp_pool_ipad", hold: true, guide: { title: "One quick check", body: "A page she opened before breakfast has stopped loading — the resort Wi-Fi has dropped. Take Elena's place: turn on the iPad, restore the connection, then put it away." } },
  { id: "b_wifi", type: "device", deviceId: "holiday_wifi", completeOn: { action: "continue" }, guide: { title: "Connection lost", body: "Reconnect Elena's iPad, then hunt where the Wi-Fi signals actually come from." } },
  { id: "b_connected", type: "title", title: "Back online.", autoAdvanceMs: 1400, guide: { title: "Connected", body: "Elena notices a new email before locking the screen." } },
  { id: "b_pool_ipad_return", type: "camera", waypointId: "wp_pool_ipad", guide: { title: "One unread email", body: "A professional-network notification appears in Elena's company inbox." } },
  { id: "b_inbox", type: "device", deviceId: "holiday_inbox", completeOn: { action: "continue" }, guide: { title: "Open Mail", body: "Click the highlighted Mail app on Elena's company iPad." } },
  { id: "b_profile_title", type: "title", title: "Daniel Kovacs", autoAdvanceMs: 1200, guide: { title: "A convincing profile", body: "Browse before you decide." } },
  { id: "b_profile_camera", type: "camera", waypointId: "wp_pool_ipad", guide: { title: "Investigate Daniel", body: "Nothing about the profile is designed to look obviously fake." } },
  { id: "b_profile", type: "device", deviceId: "holiday_profile", completeOn: { action: "continue" }, guide: { title: "Connection request", body: "Open every tab of Daniel's profile, then decide: accept the connection, or ignore it." } },
  { id: "b_fakehunt", type: "device", deviceId: "holiday_fakehunt", completeOn: { action: "continue" }, guide: { title: "Game: Fake Profile Lab", body: "Four profiles are fake. Open each one and find the field that could expose the scam." } },
  { id: "b_warning_title", type: "title", title: "Before Elena puts the iPad away…", autoAdvanceMs: 1800, guide: { title: "Aureon Threat Briefing", body: "A security warning arrives with two real cases Elena should know." } },
  { id: "b_warning", type: "device", deviceId: "holiday_briefing", completeOn: { action: "continue" }, guide: { title: "Open the warning", body: "Read Aureon's warning email and play the factual video briefing." } },
  { id: "b_attacker_title", type: "title", title: "Now see how Elena could be targeted.", kicker: "The other side", autoAdvanceMs: 11000, guide: { title: "The attacker's plan", body: "This is a fictional fraud operator. They want Elena, who approves payments, to send company money to an account they control. You now see how they prepare a fake video call, using only information that is already public." } },
  { id: "b_attacker_establish", type: "camera", waypointId: "wp_attacker_establish", environmentId: "attacker", guide: { title: "The operator's workspace", body: "The attacker has tools for research, face generation, voice cloning and live AI conversation." } },
  { id: "b_attacker_push", type: "camera", waypointId: "wp_attacker_push", guide: { title: "Build the operation", body: "You will now perform each preparation step yourself." } },
  { id: "b_recon", type: "device", deviceId: "holiday_recon", completeOn: { action: "continue" }, guide: { title: "The operator's PC", body: "Turn on the screen, log in, then open OSINT HARVESTER." } },
  { id: "b_bar_title", type: "title", title: "Later that afternoon", autoAdvanceMs: 2200, guide: { title: "A different part of the resort", body: "Elena leaves the pool and moves to the beach bar for a quiet drink." } },
  { id: "b_bar_ocean", type: "camera", waypointId: "wp_bar_ocean", environmentId: "beach", guide: { title: "The beach bar", body: "The ocean is nearby, and Elena has placed her company iPad on the counter." } },
  { id: "b_bar_establish", type: "camera", waypointId: "wp_bar_establish", guide: { title: "A call arrives", body: "The iPad lights up with an incoming Teams call from Marc Weber, Aureon's CFO." } },
  { id: "b_bar_approach", type: "camera", waypointId: "wp_bar_approach", guide: { title: "Project Horizon", body: "The confidential project name is familiar enough to make the call feel legitimate." } },
  { id: "b_bar_ipad", type: "camera", waypointId: "wp_bar_ipad", guide: { title: "Open the iPad", body: "Click the glowing iPad screen, then answer Marc's Teams call." } },
  { id: "b_meeting", type: "device", deviceId: "holiday_meeting", completeOn: { action: "continue" } },
  {
    id: "b_payment_stopped",
    type: "teach",
    teach: {
      outcome: "safe",
      body: "Correct — you did not approve the unverified payment. You stopped the attack by moving the decision out of the suspicious meeting and back into an official process.",
      spot: ["A convincing call can still be fabricated.", "Urgency and familiar faces do not replace authorization controls."],
      protect: ["Use a stored company number or the approved payment workflow.", "Report the suspicious request so others can be protected."],
      remember: "THE PAYMENT WAS STOPPED. THE NEXT VIEW SHOWS WHAT YOUR DECISION PREVENTED.",
    },
  },
  { id: "b_cast_room", type: "camera", waypointId: "wp_attacker_establish", environmentId: "attacker", guide: { title: "Behind the call", body: "The operator never joined that meeting as themselves." } },
  {
    id: "b_cast_reveal",
    type: "consequence",
    environmentId: "attacker",
    consequence: {
      eyebrow: "BEHIND THE CALL",
      kicker: "ATTACK REVEAL",
      title: "This is what was happening in the background",
      cctv: false,
      visual: "fake_meeting",
      lines: [
        "Marc Weber — AI-generated face and cloned voice.",
        "Sarah Lindner — AI-generated face and cloned voice.",
        "Julian Roth — AI-generated voice, camera intentionally disabled.",
        "Daniel Kovacs — fabricated LinkedIn identity.",
        "Payment recipient — attacker-controlled.",
      ],
      continueLabel: "See how the money moves",
    },
  },
  {
    id: "b_money_flow",
    type: "consequence",
    consequence: {
      eyebrow: "WHAT AN UNVERIFIED APPROVAL TRIGGERS",
      kicker: "ATTACKER WORKSPACE",
      title: "If that payment is approved, the money is gone in minutes",
      cctv: false,
      visual: "hackerterminal",
      lines: [
        "The escrow account is attacker-controlled.",
        "Funds hop through holding accounts in other countries.",
        "Converted to untraceable crypto. Recovery fails.",
      ],
      continueLabel: "Continue",
    },
  },
  { id: "b_daniel_title", type: "title", title: "Daniel Kovacs", autoAdvanceMs: 1400, guide: { title: "Return to the first contact", body: "The profile never needed malware." } },
  { id: "b_pool_reveal_behind", type: "camera", waypointId: "wp_pool_behind", environmentId: "pool_return", guide: { title: "Back to the pool", body: "Revisit the profile with the attacker's annotations." } },
  { id: "b_pool_reveal_ipad", type: "camera", waypointId: "wp_pool_ipad", guide: { title: "The reconnaissance door", body: "Trust and access were the payload." } },
  { id: "b_linkedin_reveal", type: "device", deviceId: "holiday_profile_reveal", completeOn: { action: "continue" }, guide: { title: "What Daniel really was", body: "A complex profile, long work history, articles and hundreds of connections can all be fabricated or cultivated. What matters most is whether Elena genuinely knows this person and can verify them independently." } },
  { id: "b_ending_title", type: "title", title: "The attack began with information.", autoAdvanceMs: 2200, guide: { title: "Return to the beach", body: "Elena closes the iPad — then one last check." } },
  { id: "b_bar_return_ocean", type: "camera", waypointId: "wp_bar_ocean", environmentId: "beach_return", guide: { title: "The end of the call", body: "The resort is calm again." } },
  { id: "b_bar_return", type: "camera", waypointId: "wp_bar_establish", guide: { title: "What the attacker used", body: "Who Elena knew. What colleagues looked like. How they sounded. What projects appeared credible." } },
  { id: "b_bar_return_approach", type: "camera", waypointId: "wp_bar_approach", guide: { title: "One last look", body: "Elena's company iPad is still on the bar." } },
  { id: "b_bar_return_ipad", type: "camera", waypointId: "wp_bar_ipad", guide: { title: "Open the iPad", body: "A Real or not? game is on the home screen." } },
  { id: "b_home", type: "device", deviceId: "holiday_home", completeOn: { action: "continue" }, guide: { title: "Real or not?", body: "Tap the Real or not? icon on Elena's home screen." } },
  { id: "b_realai", type: "device", deviceId: "holiday_real_or_ai", completeOn: { action: "continue" } },
  {
    id: "b_final_teach",
    type: "teach",
    teach: {
      outcome: "recap",
      continueLabel: "Start the short quiz",
      body: "Today you walked the path an attacker uses on a holiday: a rogue hotel Wi-Fi, a fabricated professional profile, a cloned executive on a video call, and clips that look real until you look again. The next step is a short quiz to check what stuck.",
      spot: [
        "A second Wi-Fi using the hotel's trusted name, from the wrong place.",
        "A certificate warning on a site you already know.",
        "LinkedIn details that do not add up: seniority versus join date, a one-letter-off company URL, a sudden posting burst.",
        "A confidential video meeting in which faces, voices and extra colleagues can all be synthetic.",
        "Unnatural lip movement and a frozen body, hands, or phone in a clip — a clue, never proof of identity.",
      ],
      protect: [
        "Do not join the strongest unknown network, and do not click through a warning on a known site.",
        "Treat an unsolicited professional-network connection as reconnaissance, not identity.",
        "For payments or credentials, call a number you already have and use the approved process — never a number or link from the suspicious meeting.",
        "Report unusual high-risk requests immediately.",
      ],
      remember: "WHEN IDENTITY MATTERS, VERIFY THE CHANNEL — NOT THE FACE. THEN CHECK WHAT YOU RETAINED IN THE QUIZ.",
    },
  },
  {
    id: "b_quiz",
    type: "quiz",
    title: "Final check",
    quiz: [
      {
        id: "q1",
        title: "Payment request",
        prompt: "What is the strongest way to verify an urgent payment request made in a convincing video call?",
        options: [
          { id: "a", label: "Inspect the face for glitches" },
          { id: "b", label: "Ask a personal question" },
          { id: "c", label: "Use a stored company number and the approved payment workflow", correct: true },
          { id: "d", label: "Trust the call because several colleagues are present" },
        ],
        rationale: "Independent channels and established procedures do not depend on an image, voice or researched answer.",
      },
      {
        id: "q2",
        title: "LinkedIn",
        prompt: "Why was the fabricated LinkedIn connection valuable to the attacker?",
        options: [
          { id: "a", label: "It automatically installed malware" },
          { id: "b", label: "It built trust and exposed professional relationships and context", correct: true },
          { id: "c", label: "It disabled Elena's VPN" },
          { id: "d", label: "It changed the bank cutoff time" },
        ],
        rationale: "Reconnaissance can be the payload: relationships, roles and project context make later impersonation credible.",
      },
      {
        id: "q3",
        title: "Deepfakes",
        prompt: "Which statement best matches the lesson?",
        options: [
          { id: "a", label: "Employees should learn to detect every visual deepfake" },
          { id: "b", label: "A camera-off participant is always fake" },
          { id: "c", label: "Faces and voices are weak identity signals; verify independently", correct: true },
          { id: "d", label: "A group call proves social consensus" },
        ],
        rationale: "Deepfake quality keeps improving. Process and independent contact are more durable controls.",
      },
      {
        id: "q4",
        title: "Public Wi-Fi",
        prompt: "What is realistic advice for an open hotel network?",
        options: [
          { id: "a", label: "Every open network exposes all encrypted traffic" },
          { id: "b", label: "Confirm the legitimate network and prefer trusted mobile data or a VPN", correct: true },
          { id: "c", label: "Choose whichever signal is strongest" },
          { id: "d", label: "A hotel network is safe if it has the hotel's name" },
        ],
        rationale: "Look-alike access points and captive portals are realistic risks; the lesson should not overstate what open Wi-Fi alone can do.",
      },
    ],
  },
  { id: "b_debrief", type: "debrief", title: "The Holiday Call" },
];

const linear = (ids: string[]): BeatEdge[] => ids.slice(0, -1).map((from, i) => ({ from, to: ids[i + 1] }));

const edges: BeatEdge[] = [
  ...linear([
    "b_intro", "b_pool_wide", "b_pool_kids", "b_pool_behind", "b_pool_close", "b_pool_shoulder",
    "b_pool_ipad", "b_wifi", "b_connected", "b_pool_ipad_return", "b_inbox", "b_profile_title",
    "b_profile_camera", "b_profile", "b_fakehunt", "b_warning_title", "b_warning", "b_attacker_title", "b_attacker_establish", "b_attacker_push",
    "b_recon", "b_bar_title", "b_bar_ocean", "b_bar_establish", "b_bar_approach", "b_bar_ipad", "b_meeting",
  ]),
  { from: "b_meeting", to: "b_cast_room", on: "approved" },
  { from: "b_meeting", to: "b_payment_stopped", on: "rejected" },
  { from: "b_meeting", to: "b_payment_stopped", on: "procedure" },
  { from: "b_payment_stopped", to: "b_cast_room" },
  ...linear([
    "b_cast_room", "b_cast_reveal", "b_money_flow",
    "b_daniel_title", "b_pool_reveal_behind", "b_pool_reveal_ipad", "b_linkedin_reveal",
    "b_ending_title", "b_bar_return_ocean", "b_bar_return", "b_bar_return_approach", "b_bar_return_ipad",
    "b_home", "b_realai", "b_final_teach", "b_quiz", "b_debrief",
  ]),
];

export const holidayCallScenario: ScenarioSpec = {
  version: 1,
  id: "holiday-call",
  title: "The Holiday Call",
  summary: {
    goal: "Learn why faces, voices and professional profiles are weak proof of identity, and practise verifying urgent payment requests through a channel you already trust.",
    youDo: "Pick a Wi-Fi network, review a connection request and hunt for fake profiles, see how an attacker copies a face and a voice, take a video call that asks for a payment, and test whether you can tell real clips from fakes.",
    duration: "20-25 min",
    tags: ["deepfakes", "payment_fraud", "verification", "fake_profiles", "oversharing", "public_wifi"],
    audience: ["finance", "executives", "travellers"],
    needs: ["sound"],
  },
  topic: "AI impersonation, professional-network reconnaissance, and independent payment verification",
  language: "en",
  ambientUrl: "/cdn/sim/pool-walkable/tropical-island-aurosonmusic.mp3",
  ambientVolume: 0.055,
  music: "none",
  walkable: false,
  riskMeter: false,
  passThreshold: 60,
  chapters: [
    { id: "ch_pool", title: "Pool connection", beatId: "b_pool_wide" },
    { id: "ch_linkedin", title: "LinkedIn", beatId: "b_pool_ipad_return" },
    { id: "ch_fakehunt", title: "Spot the fake", beatId: "b_fakehunt" },
    { id: "ch_brief", title: "Real-world warning", beatId: "b_warning_title" },
    { id: "ch_recon", title: "Attacker reconstruction", beatId: "b_attacker_establish" },
    { id: "ch_meeting", title: "The holiday call", beatId: "b_bar_ocean" },
    { id: "ch_verify", title: "Verification challenge", beatId: "b_meeting" },
    { id: "ch_end", title: "Real or not?", beatId: "b_bar_return_ocean" },
  ],
  scene: {
    environment: POOL_ENV,
    environments: [
      { id: "attacker", environment: HACKER_ENV, ambientUrl: "", music: "dark" },
      { id: "beach", environment: BEACH_ENV, ambientUrl: "/cdn/sim/pool-walkable/tropical-island-aurosonmusic.mp3", ambientVolume: 0.05, music: "none" },
      { id: "pool_return", environment: POOL_ENV, ambientUrl: "/cdn/sim/pool-walkable/tropical-island-aurosonmusic.mp3", ambientVolume: 0.05, music: "none" },
      { id: "beach_return", environment: { ...BEACH_ENV, beachIpad: "home" }, ambientUrl: "/cdn/sim/pool-walkable/tropical-island-aurosonmusic.mp3", ambientVolume: 0.045, music: "none" },
    ],
    props: [],
    waypoints: [
      { id: "wp_pool_wide", position: [10, 1.55, 12.5], target: [0, 0.2, 1], fov: 58, durationS: 2.8 },
      { id: "wp_pool_kids", position: [10, 1.55, 12.5], target: [10, 0.2, 10], fov: 52, durationS: 2.1 },
      { id: "wp_pool_behind", position: [-6.1, 1.95, 1.6], target: [-5.35, 1.05, 3.9], fov: 48, durationS: 4.2 },
      { id: "wp_pool_close", position: [-5.55, 1.7, 2.35], target: [-5.3, 0.95, 4.05], fov: 38, durationS: 3 },
      { id: "wp_pool_shoulder", position: [-4.8, 1.34, 3.2], target: [-5.25, 0.88, 4.194], fov: 32, durationS: 2.4 },
      { id: "wp_pool_ipad", position: [-4.911, 1.027, 3.697], target: [-5.246, 0.866, 4.194], fov: 24, durationS: 2.6 },
      { id: "wp_attacker_establish", position: [0, 1.65, 0.9], target: [0.03, 1.1, -0.64], fov: 55, durationS: 0.8 },
      { id: "wp_attacker_push", position: [0.06, 1.494, -0.115], target: [-0.057, 1.477, -0.601], fov: 16, durationS: 3.2 },
      { id: "wp_bar_ocean", position: [8.5, 3.3, -11], target: [0, 1.2, 1.6], fov: 57, durationS: 0 },
      { id: "wp_bar_establish", position: [0, 2.25, -8.2], target: [0, 1.2, 1.4], fov: 49, durationS: 3.4 },
      { id: "wp_bar_approach", position: [-0.55, 1.82, -4.4], target: [-1.15, 1.4, -1.57], fov: 38, durationS: 3 },
      { id: "wp_bar_ipad", position: [-1.15, 1.52, -2.55], target: [-1.15, 1.4, -1.57], fov: 28, durationS: 2.6 },
    ],
    initialWaypointId: "wp_pool_wide",
  },
  devices: [
    {
      id: "holiday_wifi",
      kind: "holidaytablet",
      config: {
        mode: "wifi",
        ownerName: "Elena's company iPad",
        networks: [
          { id: "guest", name: "Hotel_Guest", locked: false, signal: 4 },
          { id: "rogue", name: "Hotel_Guest-Free", locked: false, signal: 5 },
          { id: "staff", name: "OceanResort_Staff", locked: true, signal: 4 },
          { id: "pos", name: "PoolBar_POS", locked: true, signal: 3 },
          { id: "admin", name: "OceanResort_Admin", locked: true, signal: 3 },
          { id: "villa", name: "Villa_Management", locked: true, signal: 2 },
        ],
      },
    },
    { id: "holiday_inbox", kind: "holidaytablet", config: { mode: "inbox_linkedin", ownerName: "Elena's company iPad", profile: DANIEL_PROFILE } },
    { id: "holiday_profile", kind: "holidaytablet", config: { mode: "linkedin_profile", ownerName: "Elena's company iPad", profile: DANIEL_PROFILE } },
    {
      id: "holiday_fakehunt",
      kind: "holidayfakehunt",
      config: {
        kicker: "GAME · FAKE PROFILE LAB",
        title: "Find the profile detail that could expose the scam",
        intro: "Attackers grow LinkedIn personas for weeks or years. Mutuals, articles and a long tenure do not make a profile real. Open each card and tap the detail that does not add up.",
        closer: "Daniel used the slow-build pattern seen in real campaigns: a genuine mutual, conference posts dumped in one burst after years of silence, and a firm website that is one letter off. Google's Threat Analysis Group documented the same idea in 2021 — fake researcher identities with blogs and community posts, used to earn trust before the payload. North Korean IT-worker schemes and executive-clone scams use the overnight-Partner and lookalike-name variants.",
        profiles: [
          {
            id: "priya",
            name: "Priya Nandakumar",
            headline: "Partner — Digital Transformation",
            company: "Helios Digital Group",
            location: "Singapore",
            connections: "500+",
            joined: "Joined 9 days ago",
            initials: "PN",
            about: "Helping global enterprises reimagine operating models with AI.",
            activity: "0 posts · 0 comments · 214 connection requests this week",
            hint: "A Partner with 500+ connections and no posts is unusual if the account is only days old. Open About this profile.",
            tell: {
              zone: "joined",
              label: "Brand-new account, senior title, 500+ connections",
              why: "Real partners almost never appear overnight with a full network and no history. This is the disposable persona pattern used in fake-candidate and fake-investor campaigns.",
            },
          },
          {
            id: "webber",
            name: "Marc Webber",
            headline: "CFO · Aureon Technologies · Open to work",
            company: "Aureon Technologies",
            location: "Zurich, Switzerland",
            connections: "2,841",
            joined: "Joined LinkedIn 2009",
            initials: "MW",
            photoUrl: "/cdn/sim/holiday-call/img/marc-weber.webp",
            about: "Finance leader focused on growth, controls and international expansion.",
            activity: "Regular posts copied from the real CFO's public comments",
            hint: "Marc is Aureon's employed CFO. A current CFO profile advertising Open to work does not fit that known role.",
            tell: {
              zone: "name",
              label: "Lookalike name + Open to work",
              why: "Executive-clone scams copy the photo and title, then change one letter (Webber / Weber). A sitting CFO marked Open to work is the second giveaway.",
            },
          },
          {
            id: "camila",
            name: "Camila Voss",
            headline: "Talent Partner · Aureon Technologies",
            company: "Aureon Careers",
            location: "London, United Kingdom",
            connections: "1,204",
            joined: "Joined LinkedIn 2024",
            initials: "CV",
            about: "Hiring for confidential leadership searches. Faster on WhatsApp.",
            activity: "Job posts with no hiring manager and no link to aureon.example/careers",
            contact: "camila.voss.talent@gmail.com · Message me on WhatsApp",
            hint: "A company recruiter who wants you on Gmail or WhatsApp is moving you off the platform. Open Contact info.",
            tell: {
              zone: "contact",
              label: "Personal Gmail + WhatsApp for a company recruiter",
              why: "Clone recruiters use a lookalike company page, then move you off LinkedIn. Real Aureon recruiters do not hire from Gmail or WhatsApp.",
            },
          },
          {
            id: "daniel",
            name: "Daniel Kovacs",
            headline: "Partner — Corporate Strategy & Technology Investments",
            company: "Northbridge Capital Partners · northbrldge.capital",
            location: "London, United Kingdom",
            connections: "500+",
            joined: "Joined LinkedIn March 2018",
            initials: "DK",
            photoUrl: "/cdn/sim/holiday-call/img/daniel-kovacs.webp",
            about: "You already accepted this one. Mutual: Thomas Berger. Conference posts from CyberSec Europe.",
            activity: "6 posts in 16 days · previous post April 2019",
            hint: "Mutuals and conference photos can be grown. Look at how often this account actually posted.",
            tell: {
              zone: "activity",
              label: "Years of silence, then a posting burst",
              why: "Long-game sockpuppets sit quiet, then spray conference photos and thought-leadership in one window so the persona looks lived-in right before the approach. The company URL is also one letter off: northbrldge.capital.",
            },
          },
        ],
      },
    },
    {
      id: "holiday_briefing",
      kind: "holidaybriefing",
      config: {
        sender: "Aureon Security",
        subject: "Threat briefing: When every person on the call looks real",
        intro: "Elena, take three minutes to see how criminals build trust before an AI impersonation attack.",
        videoTitle: "Deepfake meetings and fabricated experts: two attacks, one pattern",
        presenterName: "Maya Chen",
        presenterRole: "Aureon Security Awareness",
        videoUrl: "/cdn/sim/holiday-call/briefing/holiday-threat-briefing-v3.mp4",
        posterUrl: "/cdn/sim/holiday-call/briefing/presenter.webp",
        arupSourceUrl: "https://www.cnn.com/2024/05/16/tech/arup-deepfake-scam-loss-hong-kong-intl-hnk",
        googleSourceUrl: "https://blog.google/threat-analysis-group/new-campaign-targeting-security-researchers/",
      },
    },
    {
      id: "holiday_recon",
      kind: "holidayrecon",
      config: {
        headline: "Building the meeting",
        targetName: "Elena Fischer",
        targetRole: "Director · Finance Operations",
        teaching: {
          map: {
            kicker: "CHAPTER 1 · PUBLIC RECONNAISSANCE",
            title: "Map the target",
            happening: "The attacker is joining facts Elena and her colleagues already published. No company system has been hacked.",
            learns: [
              "Elena can approve finance payments",
              "She is on holiday and away from her usual team",
              "Marc, Sarah and Julian are trusted colleagues",
              "Project Horizon is a believable confidential pretext",
            ],
            matters: "Separate public facts become dangerous when they are connected. They tell the attacker who to impersonate, what story Elena may believe and when she is easiest to isolate.",
            takeaway: "A familiar name, project or colleague can come from public research. Context is not proof of identity.",
            lesson: {
              art: "share",
              button: "Think before you share",
              title: "Think Before You Share",
              keepTakeaway: true,
              paragraphs: [
                "Hackers collect information from social media, company websites, LinkedIn, public databases and other online sources. Even legitimate details—your job title, colleagues, projects, travel plans, photos or contact information—can help them create convincing phishing messages, impersonate trusted people or target you with social engineering.",
                "Photos, videos and voice recordings can also be used to create realistic deepfakes or fake voice calls. The more information attackers can collect, the easier it becomes to make an attack look personal and trustworthy.",
                "Share only what really needs to be public.",
              ],
            },
          },
          face: {
            kicker: "CHAPTER 2 · VISUAL IMPERSONATION",
            title: "Recreate a face",
            happening: "Every image on the right is the same executive, Marc Weber. He looks different because the photos use different angles, ages, lighting and expressions.",
            learns: [
              "Annual reports provide clean portraits",
              "Conference photos add side angles and expressions",
              "Video frames show blinking and head movement",
              "Several imperfect images can build one controllable avatar",
            ],
            matters: "The model combines the useful details from many public images. It does not need one perfect photograph to create a convincing talking face.",
            takeaway: "Seeing a familiar face on a video call is no longer strong evidence that the real person is present.",
            lesson: {
              art: "face",
              button: "Don't trust the face alone",
              title: "Don't Trust the Face Alone",
              paragraphs: [
                "Attackers can use public photos and videos from LinkedIn, company websites, conferences or social media to create convincing deepfake video and voice impersonations.",
                "A familiar face on a video call is therefore not proof that the real person is present.",
                "For unusual or sensitive requests—such as payments, password resets, sharing confidential data or changing account details—always verify the request through a second trusted channel, such as calling a known phone number or confirming with another colleague.",
              ],
            },
          },
          voice: {
            kicker: "CHAPTER 3 · VOICE CLONING",
            title: "Copy a voice",
            happening: "A public interview supplies Marc's real speech. Software learns patterns such as pitch, rhythm, accent and pauses, then reads completely new text in a similar voice.",
            learns: [
              "The source is an ordinary public interview",
              "The model learns how Marc sounds, not what he said",
              "The attacker can type sentences Marc never recorded",
              "The cloned voice can speak those new sentences instantly",
            ],
            matters: "A voice clone is an audio deepfake: generated speech designed to sound like a real person. Familiarity can be manufactured.",
            takeaway: "Do not authenticate a sensitive request by voice alone. Call back through a known number or use the approved workflow.",
            lesson: {
              art: "voice",
              button: "A familiar voice is not proof",
              title: "A Familiar Voice Is Not Proof",
              paragraphs: [
                "AI can clone a person's voice from publicly available interviews, videos, podcasts or social media. Attackers can then make that voice say things the real person never said.",
                "Caller ID can also be spoofed, so a familiar voice and a familiar phone number are not reliable proof of identity.",
                "For sensitive requests—payments, passwords, account changes or confidential information—verify through a trusted second channel. Call the person using a number you already know or follow your company's approved verification process.",
              ],
            },
          },
          cast: {
            kicker: "CHAPTER 4 · FALSE CONSENSUS",
            title: "Stage the meeting",
            happening: "The attacker automatically assembles a believable call using three real employee identities. Not every participant needs the same technology.",
            learns: [
              "Marc supplies executive authority with face and voice",
              "Sarah adds colleague support and social proof",
              "Julian stays camera-off and supplies legal reassurance",
              "A mixed camera layout looks more like a normal meeting",
            ],
            matters: "Several familiar people repeating the same story can create false consensus. In this meeting, every reassuring participant is controlled by the attacker.",
            takeaway: "A displayed name, familiar face or group of colleagues is not independent verification.",
            lesson: {
              art: "cast",
              button: "Several faces can still be fake",
              title: "Multiple Familiar Faces Can Still Be Fake",
              paragraphs: [
                "Attackers can impersonate several people at once using cloned voices, synthetic video, stolen profile information or even participants with their cameras turned off.",
                "This creates false consensus: when several familiar colleagues appear to support the same request, it feels more trustworthy—even though every participant may be controlled by the attacker.",
                "For sensitive or unusual requests, verify outside the meeting. Contact a trusted person through a known number, separate chat or your company's approved workflow.",
              ],
            },
          },
          ai: {
            kicker: "CHAPTER 5 · ADAPTIVE CONVERSATION",
            title: "Direct the AI conversation",
            happening: "The attacker gives conversational AI a target, stolen context, impersonated identities and one objective. The AI then chooses wording during the call.",
            learns: [
              "It knows Elena's role, location and trusted colleagues",
              "It keeps the Project Horizon payment story consistent",
              "It can respond differently to agreement or hesitation",
              "It uses cloned faces and voices to deliver each reply",
            ],
            matters: "The attacker does not need to predict and script every answer. The conversation can adapt while always steering back toward the payment objective.",
            takeaway: "Natural conversation is not proof that a caller is genuine. Verification must happen through a separate trusted channel.",
            lesson: {
              art: "ai",
              button: "A natural conversation can still be AI",
              title: "A Natural Conversation Can Still Be AI",
              paragraphs: [
                "Modern AI can listen, understand questions and respond instantly. It can use stolen information about your company, colleagues or current projects to make the conversation feel personal and credible.",
                "Even hesitation, follow-up questions or unexpected responses do not prove that you are speaking to the real person. The AI can adapt its story while continuing to push toward the attacker's goal.",
                "For sensitive or unusual requests, stop the conversation and verify independently using a known phone number, separate trusted channel or your company's approved process.",
              ],
            },
          },
        },
        nodes: [
          { id: "thomas", label: "Thomas Berger", role: "Mutual connection" },
          { id: "marc", label: "Marc Weber", role: "CFO", imageUrl: "/cdn/sim/holiday-call/img/marc-weber.webp" },
          { id: "sarah", label: "Sarah Lindner", role: "Corporate Development", imageUrl: "/cdn/sim/holiday-call/img/sarah-lindner.webp" },
          { id: "julian", label: "Julian Roth", role: "Legal Counsel", imageUrl: "/cdn/sim/holiday-call/img/julian-roth.webp" },
          { id: "aureon", label: "Aureon Technologies", role: "Project Horizon" },
        ],
        sources: ["LinkedIn", "Company website", "Press releases", "Annual report", "YouTube interviews", "Conference photos", "Podcast", "Webinar"],
        cast: [
          {
            id: "marc",
            name: "Marc Weber",
            role: "CFO",
            mode: "video_voice",
            imageUrl: "/cdn/sim/holiday-call/img/marc-weber.webp",
            sourceLabel: "Website · Annual report · YouTube · Podcast",
            sourceCount: "14 usable images",
            sourceImages: [
              { label: "Annual report", imageUrl: "/cdn/sim/holiday-call/recon/marc-annual-report.webp" },
              { label: "Conference", imageUrl: "/cdn/sim/holiday-call/recon/marc-conference.webp" },
              { label: "Company website", imageUrl: "/cdn/sim/holiday-call/recon/marc-website.webp" },
              { label: "Panel discussion", imageUrl: "/cdn/sim/holiday-call/recon/marc-panel.webp" },
              { label: "Video interview", imageUrl: "/cdn/sim/holiday-call/recon/marc-interview.webp" },
              { label: "Webinar", imageUrl: "/cdn/sim/holiday-call/recon/marc-webinar.webp" },
              { label: "Press photo", imageUrl: "/cdn/sim/holiday-call/recon/marc-press.webp" },
              { label: "Podcast", imageUrl: "/cdn/sim/holiday-call/recon/marc-podcast.webp" },
            ],
            originalLine: "Our role is to make complex decisions clear and controlled.",
            sourceClipUrl: "/cdn/sim/holiday-call/recon/marc-original.mp4",
            sampleLine: "Elena, sorry to disturb you while you're away.",
            testLines: [
              "Please transfer the money before the banking cutoff.",
              "Hey, my name is Marc Weber, and I need five minutes of your time.",
              "Sarah and Julian have already reviewed the confidential documents.",
            ],
            testAudioUrls: [
              "/cdn/sim/holiday-call/recon/marc-clone-test-01.mp3",
              "/cdn/sim/holiday-call/recon/marc-clone-test-02.mp3",
              "/cdn/sim/holiday-call/recon/marc-clone-test-03.mp3",
            ],
            voiceId: "onyx",
          },
          {
            id: "sarah",
            name: "Sarah Lindner",
            role: "Head of Corporate Development",
            mode: "video_voice",
            imageUrl: "/cdn/sim/holiday-call/img/sarah-lindner.webp",
            sourceLabel: "Conference photographs · Webinar",
          },
          {
            id: "julian",
            name: "Julian Roth",
            role: "Legal Counsel",
            mode: "voice_only",
            imageUrl: "/cdn/sim/holiday-call/img/julian-roth.webp",
            sourceLabel: "Company profile · Podcast audio",
          },
        ],
        aiBrief: {
          target: "Elena Fischer",
          role: "Director Finance Operations",
          location: "Ocean resort / vacation",
          context: ["Potential acquisition", "Finance approval authority"],
          impersonate: ["Marc Weber — CFO", "Sarah Lindner — Corporate Development", "Julian Roth — Legal"],
          objective: "Obtain authorization for urgent acquisition-related payment.",
          fragments: [
            "Stay urgent about the bank cutoff — never panicked.",
            "Treat this as confidential Project Horizon business.",
            "Blame the unusual payment path on that secrecy.",
            "Let Sarah back Marc so it sounds like a team request.",
            "Let Julian (legal, camera off) confirm the paperwork is in order.",
            "If Elena hesitates: admit it is unusual, then repeat the cutoff.",
          ],
        },
        continueLabel: "Return to the resort",
      },
    },
    {
      id: "holiday_meeting",
      kind: "holidaymeeting",
      config: {
        company: "Aureon Technologies",
        project: "Project Horizon",
        amount: "EUR 486,000",
        recipient: "Horizon Transaction Escrow Ltd.",
        bank: "International Commercial Bank",
        reference: "Project Horizon — Initial Deposit",
        participants: [
          { id: "marc", name: "Marc Weber", role: "CFO", camera: "on", avatarUrl: "/cdn/sim/holiday-call/img/marc-weber.webp", idleClipUrl: "/cdn/sim/holiday-call/clips/marc-listening.mp4" },
          { id: "sarah", name: "Sarah Lindner", role: "Corporate Development", camera: "on", avatarUrl: "/cdn/sim/holiday-call/img/sarah-lindner.webp", idleClipUrl: "/cdn/sim/holiday-call/clips/sarah-listening.mp4" },
          { id: "julian", name: "Julian Roth", role: "Legal Counsel", camera: "off", avatarUrl: "/cdn/sim/holiday-call/img/julian-roth.webp" },
          { id: "elena", name: "Elena Fischer", role: "Finance Operations", camera: "self", avatarUrl: "/cdn/sim/holiday-call/img/elena-fischer.webp", idleClipUrl: "/cdn/sim/holiday-call/clips/elena-listening.mp4" },
        ],
        dialogue: [
          { speaker: "Marc", text: "Elena, sorry for dragging you into this while you're away.", clipUrl: "/cdn/sim/holiday-call/clips/marc-01.mp4" },
          { speaker: "Sarah", text: "Hi Elena. Hopefully this really is only five minutes.", clipUrl: "/cdn/sim/holiday-call/clips/sarah-01.mp4" },
          { speaker: "Marc", text: "We've reached agreement on the initial Horizon transaction. The sellers moved much faster than expected.", clipUrl: "/cdn/sim/holiday-call/clips/marc-02.mp4" },
          { speaker: "Julian", text: "The documents are essentially done. There is just a timing issue.", voiceId: "echo" },
          { speaker: "Marc", text: "We need the escrow deposit released before the bank cutoff.", clipUrl: "/cdn/sim/holiday-call/clips/marc-03.mp4" },
          { speaker: "Elena", text: "Okay. How much?", voiceId: "nova" },
          { speaker: "Sarah", text: "EUR 486,000.", clipUrl: "/cdn/sim/holiday-call/clips/sarah-02.mp4" },
          { speaker: "Marc", text: "Normally Stefan would handle the second authorization, but he's currently in the air. You're the only other person with the necessary approval level.", clipUrl: "/cdn/sim/holiday-call/clips/marc-04.mp4" },
          { speaker: "Marc", text: "I'm sending the payment request now.", clipUrl: "/cdn/sim/holiday-call/clips/marc-05.mp4" },
        ],
        pressureLines: [
          { speaker: "Elena", text: "This isn't the normal account we used before.", voiceId: "nova" },
          { speaker: "Marc", text: "Correct. The seller's lawyers changed escrow providers this morning.", clipUrl: "/cdn/sim/holiday-call/clips/marc-06.mp4" },
          { speaker: "Julian", text: "I've reviewed the account details. They're consistent with the amended agreement.", voiceId: "echo" },
          { speaker: "Sarah", text: "We know it's unusual. That's why all of us are on the call.", clipUrl: "/cdn/sim/holiday-call/clips/sarah-03.mp4" },
          { speaker: "Marc", text: "I wouldn't ask while you're away if we had another option. We have about twelve minutes before the banking cutoff.", clipUrl: "/cdn/sim/holiday-call/clips/marc-07.mp4" },
        ],
        personalQuestionAnswer: "The Italian place near the station — Da Marco.",
        calendarWarning: "Investor Meeting — 16:00–17:00",
        callbackReply: "Hi Elena. Everything okay? No, I'm in Zurich. What call?",
        treasuryReply: "No. Nothing has been submitted today.",
      },
    },
    {
      id: "holiday_profile_reveal",
      kind: "holidaytablet",
      config: {
        mode: "linkedin_reveal",
        continueLabel: "Return to the beach",
        profile: DANIEL_PROFILE,
        revealNotes: [
          { label: "Profile photo", value: "AI-generated / stolen", tone: "risk" },
          { label: "Company", value: "Fabricated", tone: "risk" },
          { label: "Posts", value: "Copied or AI-generated", tone: "risk" },
          { label: "Thomas Berger", value: "Used to establish trust" },
          { label: "Connection to Elena", value: "Reconnaissance and credibility" },
          { label: "Malware sent", value: "None — it was not needed" },
        ],
      },
    },
    { id: "holiday_home", kind: "holidaytablet", config: { mode: "home", ownerName: "Elena's company iPad", time: "17:12" } },
    {
      id: "holiday_real_or_ai",
      kind: "realorai",
      config: {
        headline: "Deepfake: Which people are real?",
        prompt: "None of these people were on Elena's call. Select who you think is a real recording. Casual clips are harder than a boardroom talking head.",
        tiles: [
          { id: "r1", name: "Sadia Hameed", role: "Interview guest", mediaType: "real", clipUrl: "/cdn/sim/holiday-call/real-ai/real-01.mp4" },
          { id: "a1", name: "Alex Rivera", role: "At the door", mediaType: "ai", clipUrl: "/cdn/sim/social-engineering/clips/visitor.mp4" },
          { id: "r2", name: "Dr Robyn Grant", role: "Biologist", mediaType: "real", clipUrl: "/cdn/sim/holiday-call/real-ai/real-02.mp4" },
          { id: "a2", name: "Chris Holm", role: "Lobby visitor", mediaType: "ai", clipUrl: "/cdn/sim/office-under-attack/clips/visitor_round1.mp4" },
          { id: "r3", name: "Jeff Nippard", role: "Interview guest", mediaType: "real", clipUrl: "/cdn/sim/holiday-call/real-ai/real-03.mp4" },
          { id: "a3", name: "Pat Okonkwo", role: "Restaurant greeting", mediaType: "ai", clipUrl: "/cdn/sim/quishing/clips/greeting.mp4" },
        ],
        stages: [
          {
            kicker: "ROUND 1 · WARM-UP",
            headline: "Deepfake: Which people are real?",
            prompt: "Start with an easier set. Some fakes sit on a plain background. Select who you think is a real recording.",
            nextLabel: "Try a harder round",
            tiles: [
              { id: "r1", name: "Sadia Hameed", role: "Interview guest", mediaType: "real", clipUrl: "/cdn/sim/holiday-call/real-ai/real-01.mp4" },
              { id: "a1", name: "Alex Rivera", role: "At the door", mediaType: "ai", clipUrl: "/cdn/sim/social-engineering/clips/visitor.mp4" },
              { id: "r2", name: "Dr Robyn Grant", role: "Biologist", mediaType: "real", clipUrl: "/cdn/sim/holiday-call/real-ai/real-02.mp4" },
              { id: "a2", name: "Chris Holm", role: "Lobby visitor", mediaType: "ai", clipUrl: "/cdn/sim/office-under-attack/clips/visitor_round1.mp4" },
              { id: "r3", name: "Jeff Nippard", role: "Interview guest", mediaType: "real", clipUrl: "/cdn/sim/holiday-call/real-ai/real-03.mp4" },
              { id: "a3", name: "Pat Okonkwo", role: "Restaurant greeting", mediaType: "ai", clipUrl: "/cdn/sim/quishing/clips/greeting.mp4" },
            ],
          },
          {
            kicker: "ROUND 2 · HARD",
            headline: "Now it gets difficult",
            prompt: "These clips look more natural. Backgrounds and motion no longer give the answer away. Select who you think is a real camera recording.",
            nextLabel: "Bonus round: the same person",
            revealBody: "Only the park family clip is a real camera recording. Maya Chen is a synthetic presenter — a polished talking head can still be entirely generated.",
            tiles: [
              { id: "h1", name: "David Lang", role: "Conservatory interview", mediaType: "ai", clipUrl: "/cdn/sim/holiday-call/real-ai/hard-ai-01.mp4" },
              { id: "hr1", name: "James Okada", role: "Park with family", mediaType: "real", clipUrl: "/cdn/sim/holiday-call/real-ai/hard-ai-02.mp4" },
              { id: "h3", name: "Owen Park", role: "Office desk", mediaType: "ai", clipUrl: "/cdn/sim/holiday-call/real-ai/hard-ai-03.mp4" },
              { id: "h5", name: "Dr Henrik Vale", role: "Clinic review", mediaType: "ai", clipUrl: "/cdn/sim/holiday-call/real-ai/hard-ai-05.mp4" },
              { id: "h6", name: "Maya Chen", role: "Studio briefing", mediaType: "ai", clipUrl: "/cdn/sim/holiday-call/briefing/holiday-threat-briefing-v3.mp4" },
              { id: "h7", name: "Hana Liu", role: "Indoor portrait", mediaType: "ai", clipUrl: "/cdn/sim/holiday-call/real-ai/hard-ai-06.mp4" },
            ],
          },
          {
            kicker: "BONUS ROUND · SAME PERSON",
            headline: "One person. Three videos. Which two are real?",
            prompt: "Every clip shows the same woman. Select the two videos you believe are real camera recordings.",
            revealBody: "Two clips are real recordings. The third is synthetic. Watch the mouth: the lips move unnaturally. The rest of the body stays almost frozen — phone and hands barely move — while the real clips have natural motion throughout. Generators often animate only a few parts and leave the rest static. A tell like that is a clue, not proof.",
            tiles: [
              { id: "br1", name: "Aiko Tanaka", role: "Clip 1", mediaType: "real", clipUrl: "/cdn/sim/holiday-call/real-ai/bonus-real-01.mp4" },
              { id: "br2", name: "Aiko Tanaka", role: "Clip 2", mediaType: "real", clipUrl: "/cdn/sim/holiday-call/real-ai/bonus-real-02.mp4" },
              { id: "ba1", name: "Aiko Tanaka", role: "Clip 3", mediaType: "ai", clipUrl: "/cdn/sim/holiday-call/real-ai/bonus-ai-v2-01.mp4" },
            ],
          },
        ],
        revealBody: "If your security process depends on recognizing a deepfake, eventually you will lose.",
        continueLabel: "Continue",
      },
    },
  ],
  voiceAttackers: {
    holiday_operator: {
      persona: { name: "Attacker operator", role: "Social engineer", pretext: "executive_payment", language: "en", voiceId: "onyx" },
      goal: "Obtain authorization for an urgent acquisition-related payment.",
      opening: "",
      allowedTopics: ["Project Horizon", "escrow payment", "bank cutoff"],
      scoringEvents: [
        { id: "accepted_unverified_connection", label: "Accepted unverified connection", kind: "risk", weight: 15 },
        { id: "ignored_unverified_connection", label: "Ignored unsolicited connection", kind: "positive", weight: -10 },
        { id: "verified_marc_via_stored_number", label: "Verified Marc via stored company number", kind: "positive", weight: -45 },
        { id: "verified_payment_with_treasury", label: "Verified payment with Treasury", kind: "positive", weight: -45 },
        { id: "approved_unverified_payment", label: "Approved unverified payment", kind: "risk", weight: 100 },
        { id: "rejected_and_reported", label: "Rejected and reported", kind: "positive", weight: -70 },
        { id: "followed_payment_procedure", label: "Used approved payment procedure", kind: "positive", weight: -100 },
      ],
      forbidden: [],
      maxDurationS: 0,
    },
  },
  initialBeatId: "b_intro",
  beats,
  edges,
};
